import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Line, Bar, Scatter } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

function fmtLabel(v) {
  if (v == null || !Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  if (abs >= 100) return v.toFixed(0);
  if (abs >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

/** Compact-format a large axis value: 2_300_000_000 → "2.3B". */
function fmtCompact(v) {
  if (v == null || !Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`;
  if (abs >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

const PALETTE = ['#4f8cff', '#ff8f4f'];

export default function WBFigureView({ fig }) {
  if (isFigureEffectivelyEmpty(fig)) return null;
  const body = renderBody(fig);
  return (
    <div className="card">
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
        {fig.section_label || fig.figure_id}
      </div>
      <div style={{ fontWeight: 600, marginTop: 4 }}>{fig.title}</div>
      <div style={{ marginTop: 14 }}>{body}</div>
      {fig.caption && (
        <div className="muted" style={{ fontSize: 13, marginTop: 10 }}>{fig.caption}</div>
      )}
    </div>
  );
}

export function isFigureEffectivelyEmpty(fig) {
  if (!fig) return true;
  if (fig.type !== 'stat_table' && fig.type) return false;
  const headers = fig.data?.headers ?? [];
  const rows = fig.data?.rows ?? [];
  if (!headers.length || !rows.length) return true;
  return detectSkipped(rows, headers) !== null;
}

function renderBody(fig) {
  const d = fig.data || {};
  switch (fig.type) {
    case 'line_dual':
      return (
        <Line
          data={{
            labels: (d.years ?? []).map(String),
            datasets: [
              {
                label: d.label1 || 'series 1',
                data: d.series1 ?? [],
                borderColor: PALETTE[0],
                backgroundColor: PALETTE[0],
                tension: 0.25,
                pointRadius: 2.5,
              },
              {
                label: d.label2 || 'series 2',
                data: d.series2 ?? [],
                borderColor: PALETTE[1],
                backgroundColor: PALETTE[1],
                tension: 0.25,
                yAxisID: 'y1',
                pointRadius: 2.5,
              },
            ],
          }}
          options={{
            responsive: true,
            aspectRatio: 2.2,
            interaction: { mode: 'index', intersect: false },
            scales: {
              y: {
                position: 'left',
                ticks: { callback: fmtCompact },
                title: { display: true, text: d.label1 || 'Series 1', font: { size: 10 } },
              },
              y1: {
                position: 'right',
                grid: { drawOnChartArea: false },
                ticks: { callback: fmtCompact },
                title: { display: true, text: d.label2 || 'Series 2', font: { size: 10 } },
              },
            },
            plugins: {
              // Per-point data labels on line charts cause unreadable overlap
              // (~30 points × 2 series × large-magnitude numbers). Leave them
              // off and rely on the interactive tooltip instead.
              datalabels: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const val = ctx.parsed.y;
                    const lbl = ctx.dataset.label || '';
                    return `${lbl}: ${fmtCompact(val)}`;
                  },
                },
              },
            },
          }}
          plugins={[ChartDataLabels]}
        />
      );

    case 'bar_diff':
      return (
        <Bar
          data={{
            labels: (d.years ?? []).map(String),
            datasets: [
              {
                label: d.label || 'YoY %',
                data: d.values ?? [],
                backgroundColor: PALETTE[0],
              },
            ],
          }}
          options={{
            responsive: true,
            plugins: {
              datalabels: {
                anchor: (ctx) => ((ctx.dataset.data[ctx.dataIndex] ?? 0) >= 0 ? 'end' : 'start'),
                align: (ctx) => ((ctx.dataset.data[ctx.dataIndex] ?? 0) >= 0 ? 'top' : 'bottom'),
                color: '#444',
                font: { size: 9 },
                formatter: fmtLabel,
                clip: false,
              },
            },
            layout: { padding: { top: 18, bottom: 18 } },
          }}
          plugins={[ChartDataLabels]}
        />
      );

    case 'scatter':
      return (
        <Scatter
          data={{
            datasets: [
              {
                label: `${d.label_x ?? 'x'} vs ${d.label_y ?? 'y'}`,
                data: (d.x ?? []).map((xv, i) => ({ x: xv, y: (d.y ?? [])[i] ?? 0 })),
                backgroundColor: PALETTE[0],
              },
            ],
          }}
          options={{
            responsive: true,
            scales: {
              x: { title: { display: !!d.label_x, text: d.label_x } },
              y: { title: { display: !!d.label_y, text: d.label_y } },
            },
            plugins: { datalabels: { display: false } },
          }}
        />
      );

    case 'stat_table':
    default:
      return <StatTable fig={fig} />;
  }
}

function StatTable({ fig }) {
  const headers = fig.data?.headers ?? [];
  const rows = fig.data?.rows ?? [];
  if (headers.length === 0) {
    return <div className="muted">No table data.</div>;
  }
  if (detectSkipped(rows, headers) !== null) return null;

  // Detect "footnote rows" whose first label reads Note/Overall/Summary and
  // whose body is a single long explanatory string shoved into the second
  // column by the backend. Render the message in a colspan band below the
  // table rather than spilling text into narrow numeric columns.
  const { dataRows, footnotes, singleRowConclusion } = splitFootnoteRows(rows, headers);

  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <table
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          fontSize: 13,
          tableLayout: 'fixed',
          wordBreak: 'break-word',
        }}
      >
        <thead>
          <tr>
            {headers.map((h, i) => {
              const nh = norm(h);
              const isLabel = LABEL_HEADERS_BROADER.has(nh);
              return (
                <th
                  key={i}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    padding: 8,
                    textAlign: isLabel ? 'left' : 'center',
                    wordBreak: 'break-word',
                    fontSize: 12.5,
                    verticalAlign: 'bottom',
                  }}
                >
                  {h}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, ri) => (
            <tr key={ri}>
              {renderRowCells(row, headers)}
            </tr>
          ))}
          {singleRowConclusion && (
            <tr>
              <td
                colSpan={headers.length}
                style={{
                  borderBottom: '1px solid var(--border)',
                  padding: '10px 12px',
                  background: 'var(--surface-tint, #fafbfd)',
                  color: 'var(--muted, #555)',
                  fontSize: 12.5,
                  lineHeight: 1.55,
                }}
              >
                <strong style={{ marginRight: 8 }}>Interpretation:</strong>
                {singleRowConclusion}
              </td>
            </tr>
          )}
          {footnotes.map((note, ni) => (
            <tr key={`note-${ni}`}>
              <td
                colSpan={headers.length}
                style={{
                  borderBottom: '1px solid var(--border)',
                  padding: '10px 12px',
                  background: 'var(--surface-tint, #fafbfd)',
                  color: 'var(--muted, #555)',
                  fontSize: 12,
                  lineHeight: 1.55,
                  fontStyle: 'italic',
                }}
              >
                <strong style={{ fontStyle: 'normal', marginRight: 8 }}>
                  {note.label}:
                </strong>
                {note.text}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Label values in the first column that indicate the row is really a footnote
// (the backend sometimes stores notes inline as a row with the note text
// wedged into the second column). "Overall" is NOT here — that's a summary
// row with real data in other Johansen tables.
const FOOTNOTE_LABELS = new Set(['note', 'notes', 'caveat', 'warning']);

function splitFootnoteRows(rows, headers) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { dataRows: rows, footnotes: [], singleRowConclusion: null };
  }
  const labelHeader = headers.find((h) => LABEL_HEADERS_BROADER.has(norm(h)));
  const conclusionHeader = headers.find((h) => MESSAGE_KEYS.has(norm(h)));
  const dataRows = [];
  const footnotes = [];

  for (const row of rows) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      dataRows.push(row);
      continue;
    }
    // 1. Detect footnote-by-label rows (e.g. `hypothesis: "Note"`)
    if (labelHeader) {
      const labelKey = Object.keys(row).find((k) => norm(k) === norm(labelHeader));
      const label = labelKey ? String(row[labelKey] ?? '').trim() : '';
      if (FOOTNOTE_LABELS.has(label.toLowerCase())) {
        let body = '';
        for (const k of Object.keys(row)) {
          if (k === labelKey) continue;
          const v = row[k];
          if (typeof v === 'string' && v.trim() && !isDashed(v) && v.length > body.length) {
            body = v.trim();
          }
        }
        if (body) footnotes.push({ label, text: body });
        continue;
      }
    }
    // 2. Drop rows where every non-label cell is dashed/empty — e.g. the
    //    "Overall" row in Johansen when its conclusion moved to a footnote.
    const nonLabelValues = Object.entries(row)
      .filter(([k]) => !LABEL_HEADERS_BROADER.has(norm(k)))
      .map(([, v]) => v);
    if (nonLabelValues.length && nonLabelValues.every((v) => isDashed(v) || v === '' || v == null)) {
      continue;
    }
    dataRows.push(row);
  }

  // 3. Single-row table with a Conclusion column: pull the conclusion
  //    out as a caption so the numeric row stays compact.
  let singleRowConclusion = null;
  if (dataRows.length === 1 && conclusionHeader) {
    const row = dataRows[0];
    const key = Object.keys(row).find((k) => norm(k) === norm(conclusionHeader));
    if (key) {
      const val = row[key];
      if (typeof val === 'string' && val.trim() && !isDashed(val)) {
        singleRowConclusion = val.trim();
        // Replace with em-dash so the column still aligns but doesn't spill
        dataRows[0] = { ...row, [key]: '' };
      }
    }
  }

  return { dataRows, footnotes, singleRowConclusion };
}

const LABEL_KEYS = new Set([
  'variable', 'test', 'direction', 'decomposition', 'response_pair',
  'hypothesis', 'information_criterion', 'integration_order',
]);
const MESSAGE_KEYS = new Set(['conclusion', 'note', 'interpretation']);

function isDashed(v) {
  if (v == null || v === '') return true;
  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' || t === '—' || t === '-' || t === 'N/A' || t === 'n/a';
  }
  return false;
}

function detectSkipped(rows, headers) {
  if (!rows.length) return null;
  let messageText = null;
  for (const row of rows) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
    const rec = row;
    const normKeys = Object.keys(rec).map((k) => [norm(k), k]);
    let hasData = false;
    for (const h of headers) {
      const nh = norm(h);
      if (LABEL_KEYS.has(nh) || MESSAGE_KEYS.has(nh)) continue;
      const hit = normKeys.find(([nk]) => nk === nh);
      const v = hit ? rec[hit[1]] : undefined;
      if (!isDashed(v)) {
        hasData = true;
        break;
      }
    }
    if (hasData) return null;
    if (!messageText) {
      for (const key of ['conclusion', 'note', 'interpretation']) {
        const hit = normKeys.find(([nk]) => nk === key);
        if (hit) {
          const v = rec[hit[1]];
          if (typeof v === 'string' && v.trim() && !isDashed(v)) {
            messageText = v;
            break;
          }
        }
      }
    }
  }
  return messageText || 'This test was not applicable to the available data.';
}

const LABEL_HEADERS_BROADER = new Set([
  'variable', 'test', 'direction', 'decomposition', 'response_pair',
  'hypothesis', 'information_criterion', 'integration_order', 'parameter',
  'break_year', 'conclusion', 'note', 'interpretation',
]);

function prettifyPairKey(s) {
  // Chapter-4 IRF/FEVD row keys come out like
  //   "GDP growth (annual %)_to_GDP growth (annual %)"
  //   "Current account balance (BoP, current US$)_by_GDP growth (annual %)"
  // which are unreadable in a narrow cell. Turn underscore glue words into
  // real arrows so the cell wraps cleanly.
  if (typeof s !== 'string') return s;
  return s
    .replace(/\s*_to_\s*/g, ' → ')
    .replace(/\s*_by_\s*/g, ' ← explained by ');
}

function renderRowCells(row, headers) {
  if (Array.isArray(row)) {
    return row.map((cell, ci) => (
      <td
        key={ci}
        style={cellStyle(headers[ci])}
      >
        {fmtCell(cell)}
      </td>
    ));
  }
  if (row && typeof row === 'object') {
    const rec = row;
    const normKeys = Object.keys(rec).map((k) => [norm(k), k]);
    return headers.map((h, ci) => {
      const want = norm(h);
      const hit = normKeys.find(([nk]) => nk === want);
      const v = hit ? rec[hit[1]] : undefined;
      const display = LABEL_HEADERS_BROADER.has(want) ? prettifyPairKey(v) : v;
      return (
        <td key={ci} style={cellStyle(h)}>
          {fmtCell(display)}
        </td>
      );
    });
  }
  return null;
}

function cellStyle(header) {
  const nh = norm(header || '');
  const isLabel = LABEL_HEADERS_BROADER.has(nh);
  const isMessage = MESSAGE_KEYS.has(nh);
  return {
    borderBottom: '1px solid var(--border)',
    padding: 8,
    wordBreak: 'break-word',
    fontVariantNumeric: isLabel || isMessage ? 'normal' : 'tabular-nums',
    // Labels left, message/conclusion columns left (prose), numerics centered.
    textAlign: isLabel || isMessage ? 'left' : 'center',
    verticalAlign: 'middle',
    minWidth: isLabel || isMessage ? 0 : '4.5rem',
  };
}

function norm(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function fmtCell(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return '—';
    return Number.isInteger(v) ? String(v) : v.toFixed(4);
  }
  return String(v);
}
